/**
 * IssuesWorkTable — die Optimierungen-Arbeitsliste ALS bestehende Produkttabelle
 * (P2a/R0): MergedProductTable + ProductBulkActionBar + Pagination, keine
 * Parallel-Liste. Clientseitige Pagination (Daten sind vollständig geladen).
 */
import { useMemo, useState } from 'react'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Pagination } from '@/components/v2/ui'
import { MergedProductTable } from '@/components/v2/products/MergedProductTable'
import { ProductBulkActionBar } from '@/components/v2/products/ProductBulkActionBar'
import type { ProductWithIssues } from '@/lib/api/channel-readiness'

import { createIssuesWorkColumns } from './issuesWorkColumns'
import { toWorkRow, type IssuesWorkRow } from './workTableAdapter'
import { useIssuesWorkSelection } from './useIssuesWorkSelection'

const PAGE_SIZE = 50

export function IssuesWorkTable({ products, onFix }: { products: ProductWithIssues[]; onFix: (row: IssuesWorkRow) => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)

  const rows = useMemo(() => products.map(toWorkRow), [products])
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageRows = useMemo(() => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE), [rows, safePage])
  const sel = useIssuesWorkSelection(rows)
  const columns = useMemo(() => createIssuesWorkColumns(t, onFix), [t, onFix])

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table API
  const table = useReactTable({
    data: pageRows, columns,
    state: { columnVisibility: { quantity_90d: false, revenue_90d: false } },
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="space-y-2" data-testid="issues-work-table">
      <MergedProductTable
        table={table}
        selectedIds={sel.selectedIds}
        onToggleSelect={sel.toggle}
        onToggleSelectAll={() => sel.toggleAll(pageRows.map((r) => r.id))}
        onRowClick={(p) => navigate(`/v2/products/${p.id}`)}
      />
      {totalPages > 1 && (
        <div className="flex justify-end">
          <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
      {sel.selectedIds.size > 0 && (
        <ProductBulkActionBar
          count={sel.selectedIds.size}
          onCreateChannelTexts={sel.createTexts}
          onBatch={sel.createImages}
          onClear={sel.clear}
        />
      )}
    </div>
  )
}
